import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Mail, MessageSquare, Phone, Share2, Megaphone, Search, Users, AtSign, Globe, UserCheck } from 'lucide-react';

const services = [
  {
    id: 'email-marketing',
    name: 'Email Marketing',
    icon: <Mail className="w-6 h-6" />,
    description: 'Powerful email campaigns with AI-driven personalization',
    plans: [
      { name: 'Basic', price: 99, features: ['Up to 10,000 emails/month', 'Basic templates', 'Email support'] },
      { name: 'Pro', price: 199, features: ['Up to 50,000 emails/month', 'Advanced automation', 'Priority support'] },
      { name: 'Enterprise', price: 299, features: ['Unlimited emails', 'Custom integration', 'Dedicated manager'] }
    ]
  },
  {
    id: 'sms-marketing',
    name: 'SMS Marketing',
    icon: <MessageSquare className="w-6 h-6" />,
    description: 'Direct SMS campaigns with high engagement rates',
    plans: [
      { name: 'Basic', price: 99, features: ['1,000 SMS/month', 'Basic templates', 'Standard delivery'] },
      { name: 'Pro', price: 199, features: ['5,000 SMS/month', 'Custom templates', 'Priority delivery'] },
      { name: 'Enterprise', price: 299, features: ['Unlimited SMS', 'API access', '24/7 support'] }
    ]
  },
  {
    id: 'whatsapp-marketing',
    name: 'WhatsApp Marketing',
    icon: <Phone className="w-6 h-6" />,
    description: 'Business WhatsApp solutions for customer engagement',
    plans: [
      { name: 'Basic', price: 99, features: ['1,000 messages/month', 'Basic templates', 'Business hours support'] },
      { name: 'Pro', price: 199, features: ['10,000 messages/month', 'Rich media support', 'Priority support'] },
      { name: 'Enterprise', price: 299, features: ['Unlimited messages', 'API integration', 'Account manager'] }
    ]
  },
  {
    id: 'social-media',
    name: 'Social Media Marketing',
    icon: <Share2 className="w-6 h-6" />,
    description: 'Comprehensive social media management and analytics',
    plans: [
      { name: 'Basic', price: 99, features: ['5 social accounts', 'Basic scheduling', 'Analytics dashboard'] },
      { name: 'Pro', price: 199, features: ['15 social accounts', 'Advanced analytics', 'Content calendar'] },
      { name: 'Enterprise', price: 299, features: ['Unlimited accounts', 'Custom reporting', 'Strategy consulting'] }
    ]
  },
  {
    id: 'advertising',
    name: 'Online Advertising',
    icon: <Megaphone className="w-6 h-6" />,
    description: 'Digital advertising management across platforms',
    plans: [
      { name: 'Basic', price: 99, features: ['2 platforms', 'Basic optimization', 'Monthly reports'] },
      { name: 'Pro', price: 199, features: ['5 platforms', 'Advanced targeting', 'Weekly optimization'] },
      { name: 'Enterprise', price: 299, features: ['All platforms', 'Custom strategy', 'Daily optimization'] }
    ]
  },
  {
    id: 'seo',
    name: 'SEO',
    icon: <Search className="w-6 h-6" />,
    description: 'Search engine optimization for better visibility',
    plans: [
      { name: 'Basic', price: 99, features: ['Keyword research', 'Monthly audit', 'Basic optimization'] },
      { name: 'Pro', price: 199, features: ['Content strategy', 'Weekly reporting', 'Link building'] },
      { name: 'Enterprise', price: 299, features: ['Custom strategy', 'Daily monitoring', 'Priority updates'] }
    ]
  },
  {
    id: 'kol',
    name: 'KOL Marketing',
    icon: <Users className="w-6 h-6" />,
    description: 'Influencer marketing and campaign management',
    plans: [
      { name: 'Basic', price: 99, features: ['Micro-influencers', 'Campaign setup', 'Basic reporting'] },
      { name: 'Pro', price: 199, features: ['Mid-tier influencers', 'Campaign optimization', 'ROI tracking'] },
      { name: 'Enterprise', price: 299, features: ['Top influencers', 'Custom campaigns', 'Full analytics'] }
    ]
  },
  {
    id: 'business-email',
    name: 'Business Email',
    icon: <AtSign className="w-6 h-6" />,
    description: 'Professional email hosting and management',
    plans: [
      { name: 'Basic', price: 99, features: ['5 email accounts', '10GB storage', 'Spam protection'] },
      { name: 'Pro', price: 199, features: ['20 email accounts', '50GB storage', 'Advanced security'] },
      { name: 'Enterprise', price: 299, features: ['Unlimited accounts', 'Unlimited storage', 'Custom domain'] }
    ]
  },
  {
    id: 'website-builder',
    name: 'Website Builder',
    icon: <Globe className="w-6 h-6" />,
    description: 'Easy-to-use website creation tools',
    plans: [
      { name: 'Basic', price: 99, features: ['5 pages', 'Basic templates', 'Custom domain'] },
      { name: 'Pro', price: 199, features: ['Unlimited pages', 'Premium templates', 'E-commerce'] },
      { name: 'Enterprise', price: 299, features: ['Custom design', 'API access', 'Priority support'] }
    ]
  },
  {
    id: 'crm',
    name: 'CRM',
    icon: <UserCheck className="w-6 h-6" />,
    description: 'Customer relationship management system',
    plans: [
      { name: 'Basic', price: 99, features: ['1,000 contacts', 'Basic automation', 'Email integration'] },
      { name: 'Pro', price: 199, features: ['10,000 contacts', 'Advanced workflow', 'API access'] },
      { name: 'Enterprise', price: 299, features: ['Unlimited contacts', 'Custom integration', 'Dedicated support'] }
    ]
  }
];

function PricingPage() {
  const location = useLocation();
  const [selectedService, setSelectedService] = useState(services[0]);

  useEffect(() => {
    if (location.state?.selectedService) {
      const service = services.find(s => s.id === location.state.selectedService);
      if (service) {
        setSelectedService(service);
        // Scroll to pricing section
        const pricingElement = document.getElementById('pricing-section');
        if (pricingElement) {
          pricingElement.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [location.state]);

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="text-center mb-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Flexible Pricing for Every Business
        </h1>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Choose the perfect plan for your business needs. All plans include our core features with different usage limits.
        </p>
      </div>

      {/* Service selector */}
      <div id="pricing-section" className="flex flex-wrap gap-4 justify-center mb-12">
        {services.map((service) => (
          <button
            key={service.id}
            onClick={() => setSelectedService(service)}
            className={`flex items-center px-4 py-2 rounded-lg transition-all ${
              selectedService.id === service.id
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {service.icon}
            <span className="ml-2">{service.name}</span>
          </button>
        ))}
      </div>

      {/* Selected service pricing */}
      <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
        {selectedService.plans.map((plan, index) => (
          <div
            key={index}
            className={`relative bg-white rounded-lg shadow-lg p-8 border-2 ${
              index === 1
                ? 'border-blue-500 transform scale-105 z-10'
                : 'border-transparent hover:border-blue-500'
            } transition-all`}
          >
            {index === 1 && (
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm whitespace-nowrap">
                  Most Popular
                </span>
              </div>
            )}
            <h3 className="text-2xl font-bold text-gray-900 mb-4">{plan.name}</h3>
            <div className="mb-6">
              <span className="text-4xl font-bold">${plan.price}</span>
              <span className="text-gray-600">/month</span>
            </div>
            <ul className="space-y-4 mb-8">
              {plan.features.map((feature, featureIndex) => (
                <li key={featureIndex} className="flex items-center">
                  <svg
                    className="w-5 h-5 text-green-500 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>
            <button className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors">
              Get Started
            </button>
          </div>
        ))}
      </div>

      <div className="mt-16 text-center">
        <p className="text-gray-600 mb-4">All plans include:</p>
        <div className="grid md:grid-cols-4 gap-4 max-w-4xl mx-auto">
          <div className="p-4">
            <h4 className="font-semibold mb-2">24/7 Support</h4>
            <p className="text-gray-600 text-sm">Get help whenever you need it</p>
          </div>
          <div className="p-4">
            <h4 className="font-semibold mb-2">99.9% Uptime</h4>
            <p className="text-gray-600 text-sm">Guaranteed reliability</p>
          </div>
          <div className="p-4">
            <h4 className="font-semibold mb-2">Secure Data</h4>
            <p className="text-gray-600 text-sm">Enterprise-grade security</p>
          </div>
          <div className="p-4">
            <h4 className="font-semibold mb-2">Regular Updates</h4>
            <p className="text-gray-600 text-sm">New features monthly</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PricingPage;